import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import t from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Icon } from './Icon';

const TRANSITION_DURATION_OPEN_MS = 350;
const TRANSITION_DURATION_CLOSE_MS = 200;

const Panel = ({ open, toggle, rear, onOpen, onClose, children }) => {
  // Flag for whether the panel is rendered.
  const [isRendered, setRendered] = useState(false);
  // Set the appropriate animation duration based on whether we're opening or
  // closing the panel.
  const animationDuration = open
    ? TRANSITION_DURATION_OPEN_MS
    : TRANSITION_DURATION_CLOSE_MS;

  // Effect to set the isRendered flag to true when we open the panel
  useEffect(() => {
    if (open) setRendered(true);
  }, [open]);

  // Close callback that sets the isRendered flag to false and calls the
  // provided onClose function
  const onClosed = useCallback(() => {
    setRendered(false);
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  // Use a bootstrap modal component to render the panel, but add a `panel`
  // class so we can style it as a panel
  return (
    isRendered && (
      <Modal
        isOpen={open}
        toggle={toggle}
        modalClassName={classNames('panel', { rear })}
        backdropClassName="panel-backdrop"
        modalTransition={{
          // Override the fade transition class since we'll be sliding the
          // panel in using css
          baseClass: 'slide',
          timeout: {
            appear: 0,
            enter: TRANSITION_DURATION_OPEN_MS,
            exit: TRANSITION_DURATION_CLOSE_MS,
          },
        }}
        backdropTransition={{
          timeout: {
            appear: 0,
            enter: TRANSITION_DURATION_OPEN_MS,
            exit: TRANSITION_DURATION_CLOSE_MS,
          },
        }}
        style={{ transitionDuration: `${animationDuration}ms` }}
        onOpened={onOpen}
        onClosed={onClosed}
      >
        {typeof children === 'function' ? children() : children}
      </Modal>
    )
  );
};

Panel.propTypes = {
  /** Should the panel be visible. */
  open: t.bool.isRequired,
  /** Function to toggle the open state of the panel. */
  toggle: t.func.isRequired,
  /** Should the panel be styled to appear behind another panel. */
  rear: t.bool,
  /** Function that will be called when the panel is opened. */
  onOpen: t.func,
  /** Function that will be called when the panel is closed. */
  onClose: t.func,
  /**
   * JSX content to render inside the panel when it is open, or a render
   * function whose results will be rendered inside the panel.
   */
  children: t.oneOfType([t.node, t.func]),
};

const PanelHeader = ({ back, children, ...props }) => (
  <ModalHeader {...props}>
    {back && (
      <ol className="breadcrumb mb-0">
        <li className="breadcrumb-item">
          <button className="btn" type="button">
            <Icon name="arrow-left" />
            <span>Back</span>
          </button>
        </li>
      </ol>
    )}
    <div>{children}</div>
  </ModalHeader>
);

PanelHeader.propTypes = {
  /** Function to toggle the open state of the panel. */
  toggle: t.func,
  /** Function that is triggered by a back button in the header. */
  back: t.string,
  /** The content for the panel title. */
  children: t.node,
};

Panel.Header = PanelHeader;
Panel.Body = ModalBody;
Panel.Footer = ModalFooter;

export { Panel };
